import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { Button, ButtonSize, ButtonVariant } from "@/shared/ui/Button";
import { useTranslation } from "react-i18next";

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onConfirmLeave: () => void;
  onCancelStay: () => void;
  message?: string;
}

export const UnsavedChangesModal: React.FC<UnsavedChangesModalProps> = ({
  isOpen,
  onConfirmLeave,
  onCancelStay,
  message,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const stayButtonRef = useRef<HTMLButtonElement>(null);
  const leaveButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "unset";
      return undefined;
    }

    const previousActiveElement = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    stayButtonRef.current?.focus();

    const focusableSelector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancelStay();
        return;
      }

      if (event.key === "Enter" && document.activeElement !== leaveButtonRef.current) {
        event.preventDefault();
        onCancelStay();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector);
      if (!focusableElements || focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // Always trap focus inside this dialog — stop outer Modal's focus-trap
      // listener (also on `document`) from intercepting Tab events.
      event.stopImmediatePropagation();

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    // Use capture phase so this handler fires BEFORE the outer Modal's bubble-phase
    // Tab-trap listener, allowing stopImmediatePropagation to prevent the outer
    // modal from stealing focus away from this dialog.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.body.style.overflow = "unset";
      previousActiveElement?.focus();
    };
  }, [isOpen, onCancelStay]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 px-4"
      data-testid="unsaved-changes-modal"
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="w-[400px] max-w-full rounded-lg bg-surface p-6 shadow-xl transition-all duration-200 ease-out animate-in fade-in zoom-in-95"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-error/10 text-error">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h2 id={titleId} className="text-xl font-semibold text-text">
              {t("common.unsavedChangesTitle", "Unsaved Changes")}
            </h2>
            <p id={descriptionId} className="mt-2 text-sm leading-6 text-text-muted" data-testid="unsaved-changes-message">
              {message || t("common.unsavedChangesModalMessage", "You have unsaved changes. Are you sure you want to leave without saving?")}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button
            ref={stayButtonRef}
            variant={ButtonVariant.Outline}
            size={ButtonSize.Md}
            onClick={onCancelStay}
            className="min-w-[96px]"
            data-testid="unsaved-changes-stay-button"
          >
            {t("common.stay", "Stay")}
          </Button>
          <Button
            ref={leaveButtonRef}
            variant={ButtonVariant.Destructive}
            size={ButtonSize.Md}
            onClick={onConfirmLeave}
            className="min-w-[96px]"
            data-testid="unsaved-changes-leave-button"
          >
            {t("common.leave", "Leave")}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UnsavedChangesModal;
